import * as React from 'react';
import { TextField, CircularProgress, makeStyles, Theme, createStyles } from '@material-ui/core';
import throttle from 'lodash/throttle';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			alignItems: 'center',
		},
		combobox: {
			flex: '1',
		},
		search: {
			marginLeft: '7px',
		}
	}),
);

interface IProps<TOption> {
	value: TOption[],
	onSelect: (value: TOption[]) => void,
	multiple?: true | false,
	placeholderText?: string,

	createRequest: (text: string) => any;
	loadOptions: (request: any, cb: (results?: TOption[]) => void) => void;

	renderOption: (option: TOption) => JSX.Element,
	getOptionLabel: (option: TOption) => string;
	getOptionSelected: (option: TOption, value: TOption) => boolean;

	error?: boolean;
	helperText?: string;
	cachePrefix?: string;

	loadOnFocus: boolean;
}

export default function LogPlusAnyResourcesAutocomplete<TOption>(props: IProps<TOption>) {
	const { t } = useTranslation();
	const classes = useStyles();

	const [loading, setLoading] = React.useState(false);
	const [options, setOptions] = React.useState<TOption[]>([]);
	const [inputValue, setInputValue] = React.useState('');

	const [cache, setCache] = React.useState<any>({});

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	};

	const fetch = React.useMemo(
		() =>
			throttle((request, callback: (results?: TOption[]) => void) => {
				props.loadOptions(request, callback);
			}, 200),
		[],
	);

	React.useEffect(() => {
		if (!props.loadOnFocus && inputValue === '') {
			setOptions([]);
			return;
		}

		let text = inputValue;
		if (cache[props.cachePrefix + "�" + text]) {
			setOptions(cache[props.cachePrefix + "�" + text]);
			return;
		};

		let active = true;
		setLoading(true);
		let request = {
			key: inputValue,
			input: inputValue,
		}
		if (props.createRequest)
			request = props.createRequest(inputValue);

		fetch(request, (results?: TOption[]) => {
			if (active) {
				var items = results || [];
				setOptions(items);
				setCache({ ...cache, [props.cachePrefix + "�" + text]: items });
			}
			setLoading(false);
		});

		return () => {
			active = false;
		};
	}, [inputValue, fetch, props.cachePrefix]);


	function singleValue(v: TOption[]): (string | NonNullable<TOption>) {
		if (!v || v.length == 0)
			return null;

		return v[0] as NonNullable<TOption>;
	}

	function getNoOptionsText(): string {

		if (inputValue)
			return t("control.autocomplete.noOptionsFoundForPhrase", { text: inputValue });
		if (!props.loadOnFocus)
			return t("control.autocomplete.enterTextToSearch") + "...";

		return t("control.autocomplete.noOptionsMessage");
	}

	let placeholder = t("common.action.search") + "...";
	if (props.placeholderText) {
		placeholder = props.placeholderText;
	};

	const renderInput = params => (
		<TextField
			{...params}
			fullWidth
			variant="outlined"
			onChange={handleChange}
			error={props.error}
			helperText={props.helperText}
			InputProps={{
				...params.InputProps,
				autoComplete: 'disabled',
				placeholder: placeholder,
				endAdornment: (
					<React.Fragment>
						{loading ? <CircularProgress color="inherit" size={20} /> : null}
						{params.InputProps.endAdornment}
					</React.Fragment>
				),
			}}
		/>
	);


	let autocomplete: React.ReactElement = null;
	if (!props.multiple) {
		autocomplete = (
			<Autocomplete
				className={classes.combobox}
				value={singleValue(props.value)}
				onChange={(event, newValue) => {
					if (!newValue)
						props.onSelect([]);
					else {
						let v = newValue as TOption;
						props.onSelect([v]);
					}
				}}
				getOptionLabel={props.getOptionLabel}
				getOptionSelected={props.getOptionSelected}

				options={options}
				loading={loading}
				filterOptions={x => x}
				autoComplete
				includeInputInList
				noOptionsText={getNoOptionsText()}
				renderInput={renderInput}
				renderOption={props.renderOption}
			/>)
	}
	else {
		autocomplete = <Autocomplete
			multiple
			className={classes.combobox}
			value={props.value}
			onChange={(event, newValue) => {
				let v = newValue as TOption[];
				props.onSelect(v);
			}}
			getOptionLabel={props.getOptionLabel}
			getOptionSelected={props.getOptionSelected}
			options={options}
			loading={loading}
			filterOptions={x => x}
			autoComplete
			includeInputInList
			noOptionsText={getNoOptionsText()}
			renderInput={renderInput}
			renderOption={props.renderOption}
		/>
	}

	return <div className={classes.root} >
		{autocomplete}
	</div>
}